/* eslint-disable you-dont-need-lodash-underscore/capitalize */
import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';
import type { GetIssueCountsResponse } from '../../../../shared/types/routes/issueCounts';

import type { IssueType, QualityIssueType } from '../../../../shared/issuesV2';
import { LICENSING_ISSUE_TYPES, QUALITY_ISSUE_TYPES, VulnSeverity, Category } from '../../../../shared/issuesV2';
import { getIssueTypeLabel } from '../../../helpers/issuesV2';
import COLORS from '../../../theme/colors';
import { QualityRequired, SecurityRequired } from '../../generic/Gates';
import type { FOSSAColor } from '../../../theme/colors';
import type { AsyncValue } from '../../../redux/asyncValue';
import LoadingBreakdown from './LoadingBreakdown';
import CategoryGraph from './CategoryGraph';

import styles from '../IssueOverview.module.less';
import { plural } from '../../../../shared/helpers';

type Props = {
  data: AsyncValue<GetIssueCountsResponse>;
  category?: Category;
};

const shortenedQualityNames: Partial<Record<QualityIssueType, string>> = {
  blacklisted_dependency: 'Blocked',
  outdated_dependency: 'Outdated',
};

const colorsMap: Record<IssueType | VulnSeverity, FOSSAColor> = {
  policy_conflict: COLORS.fossaRed120,
  policy_flag: COLORS.fossaYellow,
  unlicensed_dependency: COLORS.fossaGray80,
  outdated_dependency: COLORS.fossaGray80,
  blacklisted_dependency: COLORS.fossaRed120,
  risk_abandonware: COLORS.fossaPurple,
  'risk_empty-package': COLORS.fossaPurple40,
  'risk_native-code': COLORS.fossaPurple60,
  vulnerability: COLORS.fossaGreen,
  [VulnSeverity.Critical]: COLORS.fossaRed120,
  [VulnSeverity.High]: COLORS.fossaOrange,
  [VulnSeverity.Medium]: COLORS.fossaYellow,
  [VulnSeverity.Low]: COLORS.fossaBlue,
  [VulnSeverity.Unknown]: COLORS.fossaGray80,
};

const BreakdownSection: VoidFunctionComponent<Props> = ({ data, category }) => {
  const shouldDisplayLicensingBreakdown = !category || category === Category.Licensing;
  const shouldDisplaySecurityBreakdown = !category || category === Category.Vulnerability;
  const shouldDisplayQualityBreakdown = !category || category === Category.Quality;
  if (data.isLoading || !data.value) {
    return (
      <div className={styles.breakdownSection}>
        { shouldDisplayLicensingBreakdown && <LoadingBreakdown /> }
        { shouldDisplaySecurityBreakdown && <SecurityRequired><LoadingBreakdown /></SecurityRequired> }
        { shouldDisplayQualityBreakdown && <QualityRequired><LoadingBreakdown /></QualityRequired> }
      </div>
    );
  }

  const newActiveLicensingIssuesCount = data.value.licensing?.status.active ?? 0;
  const newActiveSecurityIssuesCount = data.value.vulnerability?.status.active ?? 0;
  const newActiveQualityIssuesCount = data.value.quality?.status.active ?? 0;

  return (
    <div className={styles.breakdownSection}>
      {shouldDisplayLicensingBreakdown &&
        <CategoryGraph
          title={`${newActiveLicensingIssuesCount} new licensing ${plural(newActiveLicensingIssuesCount, 'issue')}`}
          status={data.value.licensing?.status}
          data={LICENSING_ISSUE_TYPES.map((type) => ({
            name: getIssueTypeLabel(type as IssueType) ?? _.capitalize(type).replace('_', ' '),
            value: data.value?.licensing?.type[type] ?? 0,
            color: colorsMap[type as IssueType | VulnSeverity],
          }))} />}
      <SecurityRequired>
        {shouldDisplaySecurityBreakdown &&
        <CategoryGraph
          title={`${newActiveSecurityIssuesCount} new security ${plural(newActiveSecurityIssuesCount, 'issue')}`}
          status={data.value.vulnerability?.status}
          data={Object.values(VulnSeverity).map((type) => ({
            name: _.capitalize(type),
            value: data.value?.vulnerability?.type[type] ?? 0,
            color: colorsMap[type as IssueType | VulnSeverity],
          }))} />}
      </SecurityRequired>
      <QualityRequired>
        {shouldDisplayQualityBreakdown &&
        <CategoryGraph
          title={`${newActiveQualityIssuesCount} new quality ${plural(newActiveQualityIssuesCount, 'issue')}`}
          status={data.value.quality?.status}
          data={QUALITY_ISSUE_TYPES.map((type) => ({
            name: shortenedQualityNames[type] ?? getIssueTypeLabel(type as IssueType) ?? _.capitalize(type).replace('_', ' '),
            value: data.value?.quality?.type[type] ?? 0,
            color: colorsMap[type as IssueType | VulnSeverity],
          }))} />}
      </QualityRequired>
    </div>
  );
};

export default BreakdownSection;
